<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { convertToAttributeSlug } from 'dashboard/helper/commons.js';
import { ATTRIBUTE_MODELS, ATTRIBUTE_TYPES } from './constants';

import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    NextButton,
  },
  props: {
    onClose: {
      type: Function,
      default: () => {},
    },
    // Passes 0 or 1 based on the selected AttributeModel tab selected in the UI
    // Needs a better data type, todo: refactor this component later
    selectedAttributeModelTab: {
      type: Number,
      default: 0,
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      displayName: '',
      description: '',
      // Using the prop as default. There is no side effect here as the component
      // is destroyed completely when the modal is closed. The prop doesn't change
      // dynamically when the modal is active.
      attributeModel: this.selectedAttributeModelTab || 0,
      attributeType: 0,
      attributeKey: '',
      regexPattern: null,
      regexCue: null,
      regexEnabled: false,
      values: [],
      options: [],
      show: true,
      isTouched: false,
    };
  },

  computed: {
    ...mapGetters({
      uiFlags: 'getUIFlags',
    }),
    models() {
      return ATTRIBUTE_MODELS.map(item => ({
        ...item,
        option: this.$t(`ATTRIBUTES_MGMT.ATTRIBUTE_MODELS.${item.key}`),
      }));
    },
    types() {
      return ATTRIBUTE_TYPES.map(item => ({
        ...item,
        option: this.$t(`ATTRIBUTES_MGMT.ATTRIBUTE_TYPES.${item.key}`),
      }));
    },
    isMultiselectInvalid() {
      return this.isTouched && this.values.length === 0;
    },
    isTagInputInvalid() {
      return this.isAttributeTypeList && this.values.length === 0;
    },
    attributeListValues() {
      return this.values.map(item => item.name);
    },
    isButtonDisabled() {
      return (
        this.v$.displayName.$invalid ||
        this.v$.description.$invalid ||
        this.uiFlags.isCreating ||
        this.isTagInputInvalid
      );
    },
    keyErrorMessage() {
      if (!this.v$.attributeKey.isKey) {
        return this.$t('ATTRIBUTES_MGMT.ADD.FORM.KEY.IN_VALID');
      }
      return this.$t('ATTRIBUTES_MGMT.ADD.FORM.KEY.ERROR');
    },
    isAttributeTypeList() {
      return this.attributeType === 6;
    },
    isAttributeTypeText() {
      return this.attributeType === 0;
    },
    isRegexEnabled() {
      return this.regexEnabled;
    },
  },

  validations: {
    displayName: {
      required,
      minLength: minLength(1),
    },
    description: {
      required,
    },
    attributeModel: {
      required,
    },
    attributeType: {
      required,
    },
    attributeKey: {
      required,
      isKey(value) {
        return !(value.indexOf(' ') >= 0);
      },
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.$refs.displayNameInput?.focus?.();
    });
  },

  methods: {
    addTagValue(tagValue) {
      const tag = {
        name: tagValue,
      };
      this.values.push(tag);
      this.$refs.tagInput.$el.focus();
    },
    onTouch() {
      this.isTouched = true;
    },
    onDisplayNameChange() {
      this.attributeKey = convertToAttributeSlug(this.displayName);
    },
    toggleRegexEnabled() {
      this.regexEnabled = !this.regexEnabled;
    },
    async addAttributes() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      if (!this.regexEnabled) {
        this.regexPattern = null;
        this.regexCue = null;
      }
      try {
        await this.$store.dispatch('attributes/create', {
          attribute_display_name: this.displayName,
          attribute_description: this.description,
          attribute_model: this.attributeModel,
          attribute_display_type: this.attributeType,
          attribute_key: this.attributeKey,
          attribute_values: this.attributeListValues,
          regex_pattern: this.regexPattern
            ? new RegExp(this.regexPattern).toString()
            : null,
          regex_cue: this.regexCue,
        });
        this.alertMessage = this.$t('ATTRIBUTES_MGMT.ADD.API.SUCCESS_MESSAGE');
        this.onClose();
      } catch (error) {
        const errorMessage = error?.message;
        this.alertMessage =
          errorMessage || this.$t('ATTRIBUTES_MGMT.ADD.API.ERROR_MESSAGE');
      } finally {
        useAlert(this.alertMessage);
      }
    },
  },
};
</script>

<template>
  <woot-modal v-model:show="show" :on-close="onClose">
    <div
      class="attribute-modal flex flex-col h-auto w-full max-w-full overflow-x-hidden"
    >
      <woot-modal-header :header-title="$t('ATTRIBUTES_MGMT.ADD.TITLE')" />

      <form class="attribute-modal__form" @submit.prevent="addAttributes">
        <div class="attribute-modal__body">
          <div class="attribute-modal__grid">
            <label
              class="attribute-modal__field"
              :class="{ 'attribute-modal__field--error': v$.attributeModel.$error }"
            >
              <span class="attribute-modal__label">
                {{ $t('ATTRIBUTES_MGMT.ADD.FORM.MODEL.LABEL') }}
              </span>
              <select v-model="attributeModel" class="attribute-modal__select">
                <option v-for="model in models" :key="model.id" :value="model.id">
                  {{ model.option }}
                </option>
              </select>
              <span v-if="v$.attributeModel.$error" class="attribute-modal__error">
                {{ $t('ATTRIBUTES_MGMT.ADD.FORM.MODEL.ERROR') }}
              </span>
            </label>

            <label
              class="attribute-modal__field"
              :class="{ 'attribute-modal__field--error': v$.attributeType.$error }"
            >
              <span class="attribute-modal__label">
                {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LABEL') }}
              </span>
              <select v-model="attributeType" class="attribute-modal__select">
                <option v-for="type in types" :key="type.id" :value="type.id">
                  {{ type.option }}
                </option>
              </select>
              <span v-if="v$.attributeType.$error" class="attribute-modal__error">
                {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.ERROR') }}
              </span>
            </label>

            <label
              class="attribute-modal__field"
              :class="{ 'attribute-modal__field--error': v$.displayName.$error }"
            >
              <span class="attribute-modal__label">
                {{ $t('ATTRIBUTES_MGMT.ADD.FORM.NAME.LABEL') }}
              </span>
              <input
                ref="displayNameInput"
                v-model.trim="displayName"
                type="text"
                class="attribute-modal__input"
                :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
                @input="onDisplayNameChange"
                @blur="v$.displayName.$touch"
              />
              <span v-if="v$.displayName.$error" class="attribute-modal__error">
                {{ $t('ATTRIBUTES_MGMT.ADD.FORM.NAME.ERROR') }}
              </span>
            </label>

            <label
              class="attribute-modal__field"
              :class="{ 'attribute-modal__field--error': v$.attributeKey.$error }"
            >
              <span class="attribute-modal__label">
                {{ $t('ATTRIBUTES_MGMT.ADD.FORM.KEY.LABEL') }}
              </span>
              <input
                v-model.trim="attributeKey"
                type="text"
                class="attribute-modal__input attribute-modal__input--mono"
                :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.KEY.PLACEHOLDER')"
                @blur="v$.attributeKey.$touch"
              />
              <span v-if="v$.attributeKey.$error" class="attribute-modal__error">
                {{ keyErrorMessage }}
              </span>
            </label>
          </div>

          <label
            class="attribute-modal__field"
            :class="{ 'attribute-modal__field--error': v$.description.$error }"
          >
            <span class="attribute-modal__label">
              {{ $t('ATTRIBUTES_MGMT.ADD.FORM.DESC.LABEL') }}
            </span>
            <textarea
              v-model.trim="description"
              rows="3"
              class="attribute-modal__textarea"
              :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.DESC.PLACEHOLDER')"
              @blur="v$.description.$touch"
            />
            <span v-if="v$.description.$error" class="attribute-modal__error">
              {{ $t('ATTRIBUTES_MGMT.ADD.FORM.DESC.ERROR') }}
            </span>
          </label>

          <div v-if="isAttributeTypeList" class="attribute-modal__card">
            <p class="attribute-modal__card-title">
              {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.LABEL') }}
            </p>
            <multiselect
              ref="tagInput"
              v-model="values"
              :placeholder="
                $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.PLACEHOLDER')
              "
              label="name"
              track-by="name"
              :class="{ invalid: isMultiselectInvalid }"
              class="attribute-modal__multiselect"
              :options="options"
              multiple
              taggable
              @close="onTouch"
              @tag="addTagValue"
            />
            <span
              v-if="isMultiselectInvalid"
              class="attribute-modal__error attribute-modal__error--spaced"
            >
              {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.ERROR') }}
            </span>
          </div>

          <div v-if="isAttributeTypeText" class="attribute-modal__card">
            <label class="attribute-modal__toggle-row">
              <input
                v-model="regexEnabled"
                type="checkbox"
                class="attribute-modal__checkbox"
                @input="toggleRegexEnabled"
              />
              <span class="attribute-modal__card-title">
                {{ $t('ATTRIBUTES_MGMT.ADD.FORM.ENABLE_REGEX.LABEL') }}
              </span>
            </label>

            <div
              v-if="isRegexEnabled"
              class="attribute-modal__grid attribute-modal__grid--compact"
            >
              <label class="attribute-modal__field">
                <span class="attribute-modal__label">
                  {{ $t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_PATTERN.LABEL') }}
                </span>
                <input
                  v-model.trim="regexPattern"
                  type="text"
                  class="attribute-modal__input attribute-modal__input--mono"
                  :placeholder="
                    $t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_PATTERN.PLACEHOLDER')
                  "
                />
              </label>

              <label class="attribute-modal__field">
                <span class="attribute-modal__label">
                  {{ $t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_CUE.LABEL') }}
                </span>
                <input
                  v-model.trim="regexCue"
                  type="text"
                  class="attribute-modal__input"
                  :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_CUE.PLACEHOLDER')"
                />
              </label>
            </div>
          </div>
        </div>

        <div class="attribute-modal__actions">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('ATTRIBUTES_MGMT.ADD.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('ATTRIBUTES_MGMT.ADD.SUBMIT')"
            :disabled="isButtonDisabled"
          />
        </div>
      </form>
    </div>
  </woot-modal>
</template>

<style lang="scss" scoped>
.attribute-modal {
  background: #ffffff;
}

.attribute-modal__form {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0 2rem 1rem;
}

.attribute-modal__body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.attribute-modal__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.attribute-modal__grid--compact {
  margin-top: 1rem;
}

.attribute-modal__field {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  width: 100%;
}

.attribute-modal__label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
}

.attribute-modal__input,
.attribute-modal__select,
.attribute-modal__textarea {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #ffffff;
  color: #111827;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.attribute-modal__input,
.attribute-modal__select {
  min-height: 42px;
  padding: 0 0.875rem;
}

.attribute-modal__textarea {
  min-height: 96px;
  padding: 0.75rem 0.875rem;
  resize: vertical;
}

.attribute-modal__select:disabled,
.attribute-modal__input:disabled {
  background: #f9fafb;
  color: #6b7280;
}

.attribute-modal__input:focus,
.attribute-modal__select:focus,
.attribute-modal__textarea:focus {
  outline: none;
  border-color: #0066ff;
  box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.1);
}

.attribute-modal__field--error .attribute-modal__input,
.attribute-modal__field--error .attribute-modal__select,
.attribute-modal__field--error .attribute-modal__textarea {
  border-color: #ef4444;
}

.attribute-modal__input--mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    'Liberation Mono', 'Courier New', monospace;
}

.attribute-modal__card {
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #ffffff;
}

.attribute-modal__card-title {
  display: block;
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
}

.attribute-modal__toggle-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
}

.attribute-modal__checkbox {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  accent-color: #111827;
}

.attribute-modal__error {
  font-size: 0.75rem;
  color: #ef4444;
}

.attribute-modal__error--spaced {
  margin-top: 0.5rem;
}

.attribute-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding-top: 1rem;
}

@media (max-width: 640px) {
  .attribute-modal__form {
    padding: 0 1rem 0.875rem;
  }

  .attribute-modal__grid {
    grid-template-columns: 1fr;
  }
}

::v-deep(.modal-header),
::v-deep(.modal-body),
::v-deep(.modal-footer),
::v-deep(.modal-container) {
  background: #ffffff !important;
}

::v-deep(.multiselect) {
  margin-bottom: 0;
  min-height: 42px;
  border-radius: 10px;
  color: #111827;
}

::v-deep(.attribute-modal__multiselect .multiselect__tags) {
  min-height: 42px;
  padding: 8px 10px 0;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #ffffff;
}

::v-deep(.attribute-modal__multiselect.invalid .multiselect__tags) {
  border-color: #ef4444;
}

::v-deep(.multiselect__content-wrapper) {
  display: none;
}

::v-deep(.multiselect__input),
::v-deep(.multiselect__single) {
  background: #ffffff;
  color: #111827;
}

::v-deep(.multiselect__tag) {
  background: #111827;
}

::v-deep(input::placeholder),
::v-deep(textarea::placeholder) {
  color: #9ca3af;
}
</style>
